// src/guards/RouteGuard.tsx
import { FC, ReactNode } from "react";
import { Navigate } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import { CanAccessRoute } from "../utils/permissions";
import { Box, Typography, Button } from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";

interface RouteGuardProps {
  children: ReactNode;
  requiredRoles?: string[];
}

const RouteGuard: FC<RouteGuardProps> = ({ children, requiredRoles }) => {
  const { user, isAuthenticated } = useAuth();

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If no specific roles required, allow access
  if (!requiredRoles || requiredRoles.length === 0) {
    return <>{children}</>;
  }

  // Check if user has required role
  const hasPermission = CanAccessRoute(user, requiredRoles);

  if (!hasPermission) {
    // Show forbidden page instead of redirecting
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          textAlign: "center",
          padding: 3,
        }}
      >
        <LockIcon sx={{ fontSize: 80, color: "error.main", mb: 2 }} />
        <Typography variant="h4" gutterBottom>
          Access Denied
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 500 }}>
          You don't have permission to access this page. Please contact your administrator if you believe this is an error.
        </Typography>
        <Button variant="contained" onClick={() => window.history.back()}>
          Go Back
        </Button>
      </Box>
    );
  }

  return <>{children}</>;
};

export default RouteGuard;